import { z } from 'zod';
import { baseQueryArgs, baseExtraOptions, rsosApi } from '../rsosApi';
import { setCreateConversationLoading } from './conversationsSlice';

const DirectoryEntrySchema = z.object({
  identifier: z.string(),
  name: z.string(),
  type: z.enum(['conference', 'transfer']),
});
const DirectoryResponseSchema = z.array(DirectoryEntrySchema);

const getTCAPIBaseEndpoint = (orgName: string) => {
  return `v1/text_to_911/tcapi/psap/${orgName}`;
};

export const THIRTY_DAYS = 1000 * 60 * 60 * 24 * 30;
export const TWO_HOURS = 1000 * 60 * 60 * 2;

/**
 * Extends the rsosApi for TCAPI endpoints.
 * Authorization header must be manually included to tell RTK which token to use
 * otherwise it will use the access token
 */
export const tcapiAPI = rsosApi.injectEndpoints({
  endpoints: builder => ({
    /**
     * Fetches a phone number's ongoing conversation metadata.
     * @param callerID - phone number
     * @param orgName - Name of authority the user belongs to
     */
    getTCAPIConversationMetadata: builder.query({
      keepUnusedDataFor: Infinity,
      query: ({ callerID, orgName }) => ({
        ...baseQueryArgs,
        url: `${getTCAPIBaseEndpoint(orgName)}/session/+${callerID}`,
        method: 'GET',
      }),
      providesTags: (_result, _error, { callerID }) => [
        { type: 'TCAPIMetaData', id: callerID },
      ],
      extraOptions: {
        ...baseExtraOptions,
        maxRetries: 0, // don't retry - 404 means no active session. Revisit if 500s become an issue
      },
    }),
    /**
     * Ends a conversation with the given phone number
     * @param callerID - phone number
     * @param orgName - Name of authority the user belongs to
     */
    endTCAPIConversation: builder.mutation({
      query: ({ callerID, orgName }) => ({
        ...baseQueryArgs,
        url: `${getTCAPIBaseEndpoint(orgName)}/session/+${callerID}`,
        method: 'PUT',
        body: { status: 'inactive' },
      }),
      invalidatesTags: (_result, _error, { callerID }) => [
        { type: 'TCAPIMetaData', id: callerID },
      ],
      extraOptions: {
        ...baseExtraOptions,
      },
    }),
    /**
     * Starts a conversation with the given phone number
     */
    createTCAPIConversation: builder.mutation({
      query: ({ callerID, orgName }) => ({
        ...baseQueryArgs,
        url: `${getTCAPIBaseEndpoint(orgName)}/session/+${callerID}`,
        method: 'PUT',
        // SOUP-1191: acceptStatus must remain camelCase in the request body.
        // The backend PutTextSessionUpdate model uses extra="forbid" and only
        // accepts camelCase keys. This will be updated in SOUP-1139.
        body: { acceptStatus: 'accepted' },
      }),
      onQueryStarted: async ({ callerID }, { dispatch, queryFulfilled }) => {
        try {
          dispatch(setCreateConversationLoading({ callerID, isLoading: true }));
          await queryFulfilled;
        } finally {
          dispatch(
            setCreateConversationLoading({ callerID, isLoading: false }),
          );
        }
      },
      invalidatesTags: (_result, _error, { callerID }) => [
        { type: 'TCAPIMetaData', id: callerID },
      ],
      extraOptions: {
        ...baseExtraOptions,
      },
    }),
    /**
     * Fetches the agency directory for transfer/conference.
     * @param orgName - Name of authority the user belongs to
     */
    getTCAPIAgencyDirectory: builder.query({
      query: ({ orgName }) => ({
        ...baseQueryArgs,
        url: `v1/text_to_911/tcapi/agencies/${orgName}/directory`,
        method: 'GET',
      }),
      transformResponse: (response: unknown) => {
        const entries = DirectoryResponseSchema.parse(response);
        const filtered = entries.filter(entry => entry.type === 'conference');
        if (filtered.length === 0 && entries.length > 0) {
          if (process.env.NODE_ENV === 'development') {
            // eslint-disable-next-line no-console -- Warn if all entries were filtered out to help developers. Since may not be an error in prod, we only log for developers.
            console.error(
              'All agencies in TCAPI directory response were filtered out.',
              entries,
            );
          }
        }
        return filtered;
      },
      providesTags: ['TCAPIAgencyDirectory'],
      extraOptions: {
        ...baseExtraOptions,
      },
    }),
    /**
     * Initiates a soft transfer to another agency.
     * @param callerID - phone number
     * @param orgName - Name of authority the user belongs to
     * @param receiving_agency_identifier - identifier of the target agency
     * @param receiving_agency_name - display name of the target agency
     */
    transferTCAPIConversation: builder.mutation({
      query: ({
        callerID,
        orgName,
        receiving_agency_identifier,
        receiving_agency_name,
      }) => ({
        ...baseQueryArgs,
        url: `${getTCAPIBaseEndpoint(orgName)}/session/+${callerID}`,
        method: 'PATCH',
        body: { receiving_agency_identifier, receiving_agency_name },
      }),
      invalidatesTags: (_result, _error, { callerID }) => [
        { type: 'TCAPIMetaData', id: callerID },
      ],
      extraOptions: {
        ...baseExtraOptions,
        // Disable automatic retries — a failed transfer should surface the
        // error to the user immediately rather than silently retrying.
        maxRetries: 0,
      },
    }),
  }),
  overrideExisting: 'throw',
});

export const {
  useGetTCAPIConversationMetadataQuery,
  useLazyGetTCAPIConversationMetadataQuery,
  useEndTCAPIConversationMutation,
  useCreateTCAPIConversationMutation,
  useGetTCAPIAgencyDirectoryQuery,
  useTransferTCAPIConversationMutation,
} = tcapiAPI;

/**
 * Action creator that invalidates the cached TCAPI session metadata for a
 * given caller, forcing the active ``getTCAPIConversationMetadata`` query
 * to re-fetch. Returned action must be dispatched.
 */
export function invalidateTCAPIConversationMetadata(callerID: string) {
  return tcapiAPI.util.invalidateTags([
    { type: 'TCAPIMetaData', id: callerID },
  ]);
}
